<template>
    <div class="container">

        <Breadcrumbs class="my-6" :breadcrumbs="breadcrumbs"/>

        <h2 class="text-xl text-grey-darker uppercase">{{ type }}</h2>
        <div v-if="post.editor_choice"><i>Editor's Choice</i></div>

        <div class="row mt-4">
            <div class="lg:col-2/3 xl:col-3/4 mb-8">
                <div ref="videoContainer">
                    <div ref="title" class="bg-grey-lighter py-3 px-4">
                        <h1 class="font-thin text-2xl leading-tight">{{ post.title }}</h1>
                    </div>
                    <div v-if="fixVideo" class="lg:w-4/5 lg:px-5 xl:w-1/2 xl:px-4">
                        <div class="aspect-ratio-16/9"></div>
                    </div>
                    <div class="video-header-container" :class="{'fixed top-0 inset-x-0 w-full bg-grey-light z-10': fixVideo}">
                        <div :class="{'container': fixVideo}">
                            <div :class="{'xl:w-3/4 items-center': fixVideo}" class="row ">
                                <div :class="{'lg:col-1/2': fixVideo}" class="col-full">
                                    <div ref="video" class="relative">
                                        <VideoComponent ref="player" :src="post.video_link"/>
                                    </div>
                                </div>
                                <div v-if="fixVideo" class="hidden col lg:block">
                                    <p class="font-thin text-2xl leading-tight">{{ post.title }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="bg-grey-lighter p-4">
                    <div class="row items-center">
                        <div class="col-full sm:col">
                            <div class="row text-sm leading-tight -mt-2">
                                <div class="mt-2 col-full sm:col-auto border-r border-gray-600">
                                    Published: {{ post.created_at | date('MMMM D, YYYY') }}
                                </div>
                                <!-- <div class="mt-2 sm:col-auto border-r border-gray-600">-->
                                <!--     Page Views: {{ post.views }}-->
                                <!-- </div>-->
                                <div class="mt-2 sm:col-auto border-r border-gray-600">
                                    DOI: {{ post.doi }}
                                </div>
                                <div class="mt-2 sm:col-auto">
                                    {{ post.expertise_level | title }}
                                </div>
                            </div>
                        </div>
                        <div class="col-full sm:col-auto text-right">
                            <button class="py-2 px-3" type="button" @click.prevent="showChapters = !showChapters">
                                Surgical Videos
                                <font-awesome-icon
                                    :class="{'fa-rotate-180':showChapters}"
                                    class="text-primary ml-4"
                                    icon="chevron-down"/>
                            </button>
                        </div>
                    </div>
                    <ul v-if="showChapters" class="space-y-4 mt-6">
                        <li v-for="(videoSection, i) in videoSections">
                            <div class="row mb-4 cursor-pointer gutters-0"
                                 @click.prevent="seekInVideo(videoSection.time)">
                                <div class="col-auto mr-4">
                                    <img alt="video-icon" class="w-6" src="/images/video-icon.png">
                                </div>
                                <p class="col text-sm">
                                    <span class="font-semibold">{{ videoSection.title }}</span>
                                    <span class="text-primary">({{ videoSection.time | time }})</span>
                                </p>
                            </div>
                        </li>
                    </ul>
                </div>

                <div class="space-y-4 my-8">
                    <div v-for="section in availableSections"
                         ref="sections"
                         class="border border-grey-light rounded shadow overflow-hidden"
                         @click="handleSectionClick">

                        <button
                            class="flex justify-between items-center w-full py-3 px-4 bg-grey-lighter border-b border-grey-light"
                            @click.prevent="section.open=!section.open">
                            <span>{{ sectionTitle(section) }}</span>
                            <span class="text-sm">{{ section.open ? 'Hide -' : 'Show +' }}</span>
                        </button>
                        <div v-show="section.open" class="p-4">

                            <template v-if="section.attribute === 'video_sections'">

                                <div v-if="formattedSections.surgical_technique_prefix"
                                     class="prose prose-sm max-w-none mb-6"
                                     v-html="post.surgical_technique_prefix"/>

                                <ul class="space-y-6">
                                    <li v-for="(videoSection, i) in videoSections">
                                        <div class="row gutters-0 mb-4 cursor-pointer"
                                             @click.prevent="seekInVideo(videoSection.time)">
                                            <div class="col-auto mr-4">
                                                <img alt="video-icon" class="w-6" src="/images/video-icon.png">
                                            </div>
                                            <p class="col text-sm">
                                                <span class="font-semibold">{{ videoSection.title }}</span>
                                                <span class="text-primary">({{ videoSection.time | time }})</span>
                                            </p>
                                        </div>
                                        <div class="prose prose-sm max-w-none" v-html="formattedVideoSections[i]"></div>
                                    </li>
                                </ul>

                                <div v-if="post.surgical_technique_suffix"
                                     class="prose prose-sm max-w-none mt-6"
                                     v-html="formattedSections.surgical_technique_suffix"/>

                            </template>

                            <div
                                v-else class="prose prose-sm max-w-none"
                                v-html="formattedSections[section.attribute]"/>
                        </div>
                    </div>
                </div>

                <div class="space-y-8">
                    <div class="row text-sm">
                        <div class="col-full sm:col-auto mb-4">
                            <h2 class="font-semibold">Keywords</h2>
                        </div>
                        <div class="col-full sm:col">
                            <ul class="row gutters-1">
                                <li v-for="tag in post.tags" class="col-auto mb-2">
                                    <inertia-link
                                        :href="`/search?search=${tag.name}`"
                                        @click="onTagLinkClick"
                                        class="text-xs sm:text-sm md:text-base block py-1 px-2 rounded text-grey-darker bg-grey-light hover:text-white"
                                        :class="{'cursor-wait': disableTags}">
                                        {{ tag.name }}
                                    </inertia-link>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Quiz Section -->
                    <div id="quiz" v-if="hasQuizSection" class="mt-8">
                        <Quiz
                            :questions="post.questions"
                            :post-title="post.title"
                            @seek-video="seekInVideo"/>
                    </div>

                    <p class="text-sm">
                        &copy; The Author {{ post.created_at | date('YYYY') }}. Published by <i>MMCTS</i> on behalf
                        of the European Association for Cardio-Thoracic Surgery. All rights reserved.
                    </p>

                    <section v-if="post.related && post.related.length">

                        <h2 class="text-lg py-2 px-3 bg-grey-lighter mb-6">You may also like</h2>

                        <div class="row space-y-6 md:space-y-0">
                            <template v-for="related in post.related">
                                <div class="md:col-1/3 text-grey-darker hover:text-grey-darker" :key="related.id">
                                    <PostTile :post="related" :split="false"/>
                                </div>
                            </template>
                        </div>
                    </section>
                </div>
            </div>
            <div class="lg:col-1/3 xl:col-1/4 mb-8">
                
                <h3 v-if="authors.length" class="mb-2">Author Profiles</h3>
                <ul v-if="authors.length" class="space-y-2 mb-6">
                    <li v-for="author in authors">
                        <button
                            class="flex justify-between items-center w-full py-3 px-4 bg-grey-lighter border-b border-grey-light"
                            @click.prevent="author.open=!author.open">
                            <span class="text-left">{{ author.name }}</span>
                            <span class="text-sm">{{ author.open ? 'Hide -' : 'Show +' }}</span>
                        </button>
                        <div v-show="author.open" class="p-4">
                            <div class="row">
                                <div class="col-auto">
                                    <div :class="{'w-32 h-32': author.portrait}">
                                        <img
                                            v-if="author.portrait" :alt="author.name"
                                            :src="`/media/${author.portrait.uuid}`"
                                            class="max-h-full">
                                        <font-awesome-icon v-else icon="user-md" size="3x"/>
                                    </div>
                                </div>
                                <div class="col">
                                    <inertia-link
                                        :href="`/author/${author.id}`"
                                        class="text-primary underline hover:text-primary-dark">
                                        View profile
                                    </inertia-link>
                                </div>
                            </div>
                        </div>
                    </li>
                </ul>

                <ul v-if="availableSections.length" class="hidden leading-tight lg:block mb-6">
                    <li v-for="(section, i) in availableSections"
                        class="py-3 border-b border-dotted border-grey-dark last:border-0 last:pb-0">
                        <button
                            class="flex justify-start items-start w-full p-1 text-left text-sm"
                            @click.prevent="scrollTo($refs.sections[i])">
                            <font-awesome-icon class="text-primary mr-4" icon="chevron-right"/>
                            <span class="flex-1 font-semibold">{{ sectionTitle(section) }}</span>
                        </button>
                    </li>
                    <li v-if="hasQuizSection" class="py-3 border-b border-dotted border-grey-dark last:border-0 last:pb-0">
                        <button
                            class="flex justify-start items-start w-full p-1 text-left text-sm"
                            @click.prevent="scrollToQuizSection">
                            <font-awesome-icon class="text-primary mr-4" icon="chevron-right"/>
                            <span class="flex-1 font-semibold">Quiz: NEW FEATURE</span>
                        </button>
                    </li>
                </ul>

            </div>
        </div>
        <Modal :show="showReferencesModal" @close="showReferencesModal = false">
            <div v-if="selectedReferences.length" class="prose p-4">
                <ol class="list-custom">
                    <li v-for="refId in selectedReferences"
                        :data-index="refId"
                        v-html="references[refId]"/>
                </ol>
            </div>
        </Modal>
    </div>
</template>

<script>

import Layout from "@app/Shared/Components/Layout";
import Breadcrumbs from "@app/Shared/Components/Breadcrumbs";
import HasMetaData from "@app/Shared/Mixins/HasMetaData";
import VideoComponent from "@global/Components/VideoComponent";
import PostTile from "@app/Shared/Components/PostTile";
import Modal from "@app/Shared/Components/Modal";
import Quiz from "@app/Shared/Components/Quiz";
import {PostType} from "@admin/Lib/Enums";
import {startCase} from "lodash";
import {Inertia} from "@inertiajs/inertia";

let shareThisCleanupTimer = null;
let postPageActive = false;

function removeStickyShareButtons() {
    document.querySelectorAll('.st-sticky-share-buttons, .sharethis-sticky-share-buttons, #st-2').forEach((el) => {
        el.remove();
    });
}

function stopShareThisCleanup() {
    if (shareThisCleanupTimer) {
        clearInterval(shareThisCleanupTimer);
        shareThisCleanupTimer = null;
    }
}

function startShareThisCleanup() {
    stopShareThisCleanup();

    let attempts = 0;
    shareThisCleanupTimer = setInterval(() => {
        if (postPageActive) {
            stopShareThisCleanup();
            return;
        }

        removeStickyShareButtons();

        attempts += 1;
        if (attempts >= 24) {
            stopShareThisCleanup();
        }
    }, 250);
}


export default {

    mixins: [HasMetaData],

    metaInfo() {
        return {
            title: _.get(this.$page, 'meta.title', 'MMCTS'),
            script: [
                {
                    vmid: 'sharethis-post-detail',
                    src: 'https://platform-api.sharethis.com/js/sharethis.js#property=69dc99738464330f2e14d33a&product=sop',
                    async: true
                }
            ]
        };
    },

    props: {
        post: Object,
    },

    data() {
        return {
            fixVideo: false,
            showChapters: false,
            sections: [
                {
                    title: 'Abstract',
                    attribute: 'abstract',
                    open: true
                },
                {
                    title: 'Introduction',
                    attribute: 'introduction',
                    open: true
                },
                {
                    title: 'Patient Presentation',
                    attribute: 'patient_presentation',
                    open: true
                },
                {
                    title: 'Surgical Technique & Videos',
                    attribute: 'video_sections',
                    open: true
                },
                {
                    title: 'Outcome & Discussion',
                    attribute: 'outcome_and_discussion',
                    open: true
                },
                {
                    title: 'References',
                    attribute: 'references',
                    open: true
                },
                {
                    title: 'Acknowledgements',
                    attribute: 'acknowledgements',
                    open: true
                },
                {
                    title: 'Conflicts of Interest',
                    attribute: 'conflicts_of_interest',
                    open: true
                },
                {
                    title: 'Funding Disclosures',
                    attribute: 'funding_disclosures',
                    open: true
                },
                {
                    title: 'Editorial Commentary',
                    attribute: 'editorial_commentary',
                    open: true
                },
                {
                    title: 'Author & Post Information',
                    attribute: 'author_and_post_information',
                    open: true
                },
            ],
            authors: [],
            authorlists: [],
            selectedReferences: [],
            showReferencesModal: false,
            disableTags: false,
            shareThisFixTimer: null,
        };
    },

    created() {
        Inertia.on('start', event => {
            this.disableTags = true;
        });

        Inertia.on('finish', event => {
            this.disableTags = false;
        });
    },

    mounted() {

        window.addEventListener('resize', this.checkFixedVideo);
        window.addEventListener('scroll', this.checkFixedVideo);

        this.authors = _.sortBy(this.post.authors, ['pivot.position', 'name', 'id']).map(item => {
            let author = _.cloneDeep(item)
            author.open = false;

            return author;
        });
        
        this.authorlists = _.sortBy(this.post.authorlists, ['pivot.position', 'name', 'id']).map(item => {
            let authorlists = _.cloneDeep(item)
            authorlists.open = false;

            return authorlists;
        });
        this.$nextTick(() => {
            this.scrollToQuizFromHash(0);
        });
        postPageActive = true;
        stopShareThisCleanup();
        this.injectShareThisStyles();
        this.reinitializeShareThis();
        this.applyShareThisLayoutFix();
    },

    layout: Layout,

    components: {
        PostTile,
        Breadcrumbs,
        VideoComponent,
        Modal,
        Quiz
    },

    filters: {
        date: (date, format) => dayjs(date).format(format),
        time: time => Math.floor(time / 60) + ':' + _.padStart(time % 60, 2, '0'),
        title: _.startCase
    },

    methods: {
        onTagLinkClick(e) {
            if (this.disableTags) {
                e.preventDefault();
            }
        },
        shouldShowSection(section) {
            switch (section.attribute) {
                case 'video_sections':
                    return this.post.video_sections.length
                default:
                    return !!this.post[section.attribute];
            }
        },
        checkFixedVideo() {
            this.fixVideo = this.$refs.videoContainer.getBoundingClientRect().top + this.$refs.title.clientHeight <= 0;
        },
        getOffsetTop(el) {
            let offsetTop = 0;
            while (el) {
                offsetTop += el.offsetTop;
                el = el.offsetParent;
            }
            return offsetTop;
        },
        scrollTo(el) {
            this.fixVideo = true;

            this.$nextTick(() => {
                window.scrollTo(0, this.getOffsetTop(el) - this.$refs.video.clientHeight - 16)
            });
        },
        scrollToQuizSection() {
            const quizElement = document.getElementById('quiz');
            const fixedHeader = document.querySelector('.video-header-container');

            if (!quizElement || !fixedHeader) {
                return;
            }

            const offset = fixedHeader.offsetHeight;
            const elementPosition = quizElement.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: Math.max(0, elementPosition - offset),
                behavior: 'smooth'
            });
        },
        seekInVideo(position) {
            this.$refs.player.seek(position);
        },
        validReferences(references) {
            if (!references) {
                return false;
            }

            const ids = this.parseReferences(references);

            if (ids.length < 1) {
                return false;
            }

            const refs = this.references;

            for (let id of ids) {
                if (!refs.hasOwnProperty(id)) {
                    return false;
                }
            }

            return true;
        },
        parseReferences(references) {
            try {
                return _.chain(references)
                    .trim('[]')
                    .split(',')
                    .flatMap(reference => {

                        let items = reference.split(/&ndash;|–/).map(item => {
                            const id = parseInt(item.replace(/\s*&nbsp;*/g, ''));

                            if (isNaN(id)) {
                                throw new Error('Not a valid id.')
                            }

                            return id;
                        });

                        if (items.length === 1) {
                            return items[0];
                        }

                        return _.range(items[0], items[1] + 1);
                    })
                    .value();
            } catch (e) {
                return [];
            }
        },
         formatSection(section) {
            if (typeof section === 'string') {
                let matches = section.match(/\[[\d\s]+[^\.\]]*\]/gm);
                if (matches) {
                        matches = matches.filter((v, i, a) => a.indexOf(v) === i);
                        for (let match of matches) {
                                if (this.validReferences(match)) {
                                      // section = section.replace(match, `<button data-references="${match}" class="text-primary">${match}</button>`);
                                       section = section.replaceAll(match, `<button data-references="${match}" class="text-primary">${match}</button>`); 
                                }
                        }
                }
            }
                
            return section;
        },
        handleSectionClick(e) {
            const references = e.target.dataset.references
            if (this.validReferences(references)) {
                e.preventDefault();
                this.selectedReferences = this.parseReferences(references);
                this.showReferencesModal = true;
            }
        },
        sectionTitle(section) {
            if (section.attribute === 'author_and_post_information') {
                return `Author & ${startCase(this.type)} Information`;
            }

            return section.title;
        },
        scrollToQuizFromHash(attempt = 0) {
            const scrollTarget = new URLSearchParams(window.location.search).get('scroll');
            if (scrollTarget === 'quiz') {
                const quizElement = document.getElementById('quiz');
                const fixedHeader = document.querySelector('.video-header-container');

                if (!quizElement || !fixedHeader) {
                    if (attempt < 12) {
                        setTimeout(() => this.scrollToQuizFromHash(attempt + 1), 120);
                    }
                    return;
                }

                const offset = fixedHeader.offsetHeight;
                const elementPosition = quizElement.getBoundingClientRect().top + window.scrollY;
                window.scrollTo({
                    top: Math.max(0, elementPosition - offset),
                    behavior: attempt === 0 ? 'auto' : 'smooth'
                });
            }
        },
        injectShareThisStyles() {
            const styleId = 'post-sharethis-layout-fix';
            const existing = document.getElementById(styleId);
            if (existing) {
                existing.remove();
            }

            // Keep ShareThis sticky bar as a compact vertical column of square icons.
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
                .st-sticky-share-buttons,
                .sharethis-sticky-share-buttons {
                    display: flex !important;
                    flex-direction: column !important;
                    align-items: stretch !important;
                    width: 48px !important;
                    max-width: 48px !important;
                    min-width: 48px !important;
                }

                .st-sticky-share-buttons .st-btn,
                .sharethis-sticky-share-buttons .st-btn {
                    flex: 0 0 48px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    box-sizing: border-box !important;
                    width: 48px !important;
                    min-width: 48px !important;
                    max-width: 48px !important;
                    height: 48px !important;
                    padding: 12px !important;
                }

                .st-sticky-share-buttons .st-btn > img,
                .st-sticky-share-buttons .st-btn > svg,
                .sharethis-sticky-share-buttons .st-btn > img,
                .sharethis-sticky-share-buttons .st-btn > svg {
                    width: 24px !important;
                    height: 24px !important;
                }

                .st-sticky-share-buttons .st-label,
                .sharethis-sticky-share-buttons .st-label {
                    display: none !important;
                }
            `;
            document.head.appendChild(style);
        },
        reinitializeShareThis() {
            // First hard load: ShareThis script (injected via metaInfo) will auto-render.
            // SPA remount: the script is already loaded, so we must explicitly re-create the bar.
            if (!window.__sharethis__) {
                return;
            }

            removeStickyShareButtons();

            try {
                if (typeof window.__sharethis__.initialize === 'function') {
                    window.__sharethis__.initialize();
                }
            } catch (e) {
                // ignore ShareThis internal errors
            }

            try {
                if (typeof window.__sharethis__.load === 'function') {
                    window.__sharethis__.load('sop');
                }
            } catch (e) {
                // ignore ShareThis internal errors
            }

            try {
                if (typeof window.__sharethis__.loadSsb === 'function') {
                    window.__sharethis__.loadSsb();
                }
            } catch (e) {
                // ignore ShareThis internal errors
            }

            // Keep a single sticky bar if ShareThis creates duplicates.
            this.$nextTick(() => {
                const bars = document.querySelectorAll('.st-sticky-share-buttons, .sharethis-sticky-share-buttons');
                bars.forEach((el, index) => {
                    if (index < bars.length - 1) {
                        el.remove();
                    }
                });
            });
        },
        applyShareThisLayoutFix() {
            if (this.shareThisFixTimer) {
                clearInterval(this.shareThisFixTimer);
                this.shareThisFixTimer = null;
            }

            let attempts = 0;
            const applyCompactLayout = () => {
                const container = document.querySelector('.st-sticky-share-buttons, .sharethis-sticky-share-buttons');
                if (container) {
                    container.style.setProperty('display', 'flex', 'important');
                    container.style.setProperty('flex-direction', 'column', 'important');
                    container.style.setProperty('width', '48px', 'important');
                    container.style.setProperty('max-width', '48px', 'important');
                    container.style.setProperty('min-width', '48px', 'important');
                }

                document.querySelectorAll('.st-sticky-share-buttons .st-btn, .sharethis-sticky-share-buttons .st-btn').forEach((button) => {
                    button.style.setProperty('flex', '0 0 48px', 'important');
                    button.style.setProperty('width', '48px', 'important');
                    button.style.setProperty('min-width', '48px', 'important');
                    button.style.setProperty('max-width', '48px', 'important');
                    button.style.setProperty('height', '48px', 'important');
                    button.style.setProperty('padding', '12px', 'important');
                    button.style.setProperty('display', 'flex', 'important');
                    button.style.setProperty('align-items', 'center', 'important');
                    button.style.setProperty('justify-content', 'center', 'important');
                    button.style.setProperty('box-sizing', 'border-box', 'important');
                });

                attempts += 1;
                if (attempts >= 20) {
                    clearInterval(this.shareThisFixTimer);
                    this.shareThisFixTimer = null;
                }
            };

            applyCompactLayout();
            this.shareThisFixTimer = setInterval(applyCompactLayout, 300);
        },
        teardownShareThis() {
            if (this.shareThisFixTimer) {
                clearInterval(this.shareThisFixTimer);
                this.shareThisFixTimer = null;
            }
            const styleTag = document.getElementById('post-sharethis-layout-fix');
            if (styleTag) {
                styleTag.remove();
            }

            postPageActive = false;
            removeStickyShareButtons();
            startShareThisCleanup();
        },
    },
    computed: {
        hasQuizSection() {
            return !!this.post.publish_quiz && this.post.questions && this.post.questions.length > 0;
        },
        availableSections() {
            return this.sections.filter(item => this.shouldShowSection(item));
        },
        breadcrumbs() {
            let items = [
                {label: 'Home', href: '/'},
            ];

            return _.chain(this.post.categories)
                .groupBy('parent_id')
                .reduce((carry, items) => {

                    let parent = items[0].parent;

                    carry.push({
                        label: parent.name,
                        suffix: `(${items.map(item => item.name).sort().join(') (')})`,
                        href: `/categories/${parent.slug}`
                    });

                    return carry;

                }, items)
                .value()
        },
        videoSections() {
            return _.sortBy(this.post.video_sections, 'time');
        },
        formattedSections() {
            return [
                'abstract',
                'introduction',
                'patient_presentation',
                'outcome_and_discussion',
                'references',
                'acknowledgements',
                'conflicts_of_interest',
                'funding_disclosures',
                'editorial_commentary',
                'author_and_post_information',
                'surgical_technique_prefix',
                'surgical_technique_suffix',
            ].reduce((carry, item) => {
                carry[item] = this.formatSection(this.post[item]);

                return carry;
            }, {})
        },
        formattedVideoSections() {
            return this.post.video_sections.map(section => this.formatSection(section.body), {})
        },
        references() {
            if (!this.post.references) {
                return [];
            }

            const node = document.createElement('body');
            node.innerHTML = this.post.references;

            return Array.prototype.slice.call(node.querySelectorAll('body>ol>li')).reduce((carry, item, index) => {
                carry[index + 1] = item.innerHTML;

                return carry;
            }, {})
        },
        type() {
            switch (this.post.type) {
                case PostType.Tutorial:
                    return 'Tutorial'
                case PostType.CaseReport:
                    return 'Case report'
            }
        }
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.checkFixedVideo)
        window.removeEventListener('scroll', this.checkFixedVideo)
        this.teardownShareThis();
    }
};

</script>
